/**
 * URL Helper Functions
 * Single Responsibility: URL parsing and manipulation utilities
 */

/**
 * Extract subdomain from base URL for Ably
 * @param {string} baseUrl - Base URL (e.g., "https://store.example.com")
 * @returns {string} Subdomain (e.g., "store") or "store" as fallback
 */
export function extractSubdomainFromBaseUrl(baseUrl) {
  if (!baseUrl || typeof baseUrl !== "string") {
    return "store";
  }

  try {
    const urlPart = baseUrl.split("//")[1];
    if (!urlPart) {
      return "store";
    }
    const subdomain = urlPart.split(".")[0].replace(":", "_");
    return subdomain || "store";
  } catch (error) {
    console.warn("[urlHelper] Error extracting subdomain:", error);
    return "store";
  }
}

/**
 * Builds a store's dashboard base URL from its alias.
 * @param {string} alias - Store alias identifier
 * @returns {string} Dashboard base URL (e.g., "https://mystore.biyo.co")
 */
export function buildDashboardLink(alias) {
  return `https://${alias}.biyo.co`;
}

/**
 * Returns the build-time fixed store alias, if configured.
 * When set, the app runs in single-store mode (custom domain root, no
 * /{storeAlias} path segment).
 * @returns {string} The configured alias, or "" in multi-tenant mode.
 */
export function getFixedStoreAlias() {
  return process.env.VUE_APP_STORE_ALIAS || "";
}

/**
 * Whether the app is running in single-store (fixed alias) mode.
 * @returns {boolean}
 */
export function isFixedStoreMode() {
  return !!getFixedStoreAlias();
}

/**
 * Resolves the active store alias, preferring the fixed alias (single-store
 * mode) and falling back to the route param (multi-tenant mode).
 * @param {Object} route - Vue route object
 * @returns {string} The resolved store alias, or "".
 */
export function resolveStoreAlias(route) {
  return getFixedStoreAlias() || route?.params?.storeAlias || "";
}
